// A11 — Geografiya va davlatlar. Module-local data layer (RateLimitsScreen /
// developers/devApi.ts naqshi): module-local RU strings + safeList fail-soft.
// Does NOT touch `App.tsx` / `screenStatus.ts` / nav / `i18n/*` (C-session HOT).
//
// Data manbalari:
//   • REAL — `GET /admin/countries` (`{countries:[…]}`) davlat ro'yxati +
//     ro'yxatdan-o'tganlar soni; blok = four-eyes producer (`createFourEyesRequest`).
//     `fetchCountries`/`CountryRow` — `@/lib/adapters` dan qayta ishlatiladi.
//   • GREENFIELD — `/admin/geo/*` hali chiqmagan → `safeList` bo'sh massivga
//     tushadi (fail-soft). Backend chiqqanda aynan mos keladi (Ads/SEO ham
//     shунday boshlangan). Faqat GET; mutatsiyalar backend chiqqach ulanadi.
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'
import { fetchCountries, type CountryRow } from '@/lib/adapters'

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ── REAL: davlat ro'yxati (xarita/cheklov ekranlari uchun) ──────── */

/** A11.01 — barcha davlatlar (xarita ko'rinishi). REAL `/admin/countries`;
 *  bir sahifada 1000 tagacha (≈200 davlat) → hammasi keladi, users bo'yicha. */
export async function fetchAllCountries(signal: AbortSignal): Promise<CountryRow[]> {
  const res = await fetchCountries({
    filters: [],
    sort: [{ key: 'users', desc: true }],
    page: 0,
    pageSize: 1000,
    search: '',
    signal,
  })
  return res.rows
}

export type GeoOverlay = Record<string, unknown> & { code?: string }

/** REAL davlat qatori + greenfield per-davlat overlay (`ov`). */
export interface CountryOverlayRow extends CountryRow {
  ov?: GeoOverlay
}

/** Cheklov/rollout/lokal ekranlari uchun: REAL `/admin/countries` sahifasini
 *  greenfield `path`/`key` overlay'i bilan ISO2 kod bo'yicha birlashtiradi.
 *  Sahifalash/qidiruv REAL `fetchCountries` dan — overlay bo'sh bo'lsa ham
 *  ro'yxat to'liq ishlaydi (fail-soft). */
export function fetchCountriesOverlay(
  path: string,
  key: string,
): (params: FetchParams) => Promise<FetchResult<CountryOverlayRow>> {
  return async (params) => {
    const [page, overlay] = await Promise.all([
      fetchCountries(params),
      safeList<GeoOverlay>(path, key, params.signal),
    ])
    const map = new Map<string, GeoOverlay>()
    for (const o of overlay) {
      const code = String(o.code ?? '').toUpperCase()
      if (code) map.set(code, o)
    }
    return {
      total: page.total,
      rows: page.rows.map((c) => ({ ...c, ov: map.get(c.code.toUpperCase()) })),
    }
  }
}

/** Overlay qatoridan matn maydonini xavfsiz o'qish (massiv → vergul bilan). */
export function ovText(row: CountryOverlayRow, field: string): string {
  const v = row.ov?.[field]
  if (v == null) return ''
  if (Array.isArray(v)) return v.map(String).join(', ')
  return String(v)
}

/* ── GREENFIELD: mustaqil ro'yxatlar ─────────────────────────────── */

/** A11.10 — telefon operatori (MCC/MNC yo'naltirgich). */
export interface OperatorRow {
  id: number
  country_code: string
  name: string
  mcc_mnc: string
  subscribers: number
  status: 'active' | 'inactive'
}

export function fetchOperators(params: FetchParams): Promise<FetchResult<OperatorRow>> {
  return safeList<OperatorRow>('/admin/geo/operators', 'operators', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.country_code} ${r.mcc_mnc}`),
  )
}

/** A11.12 — bloklangan raqam diapazoni (prefiks/MSISDN oralig'i). */
export interface BlockedRangeRow {
  id: number
  country_code: string
  prefix: string
  reason: string
  blocked_at: string
}

export function fetchBlockedRanges(params: FetchParams): Promise<FetchResult<BlockedRangeRow>> {
  return safeList<BlockedRangeRow>('/admin/geo/blocked-ranges', 'ranges', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.prefix} ${r.country_code} ${r.reason}`),
  )
}

/** A11.19 — huquqiy talab (rezidentlik/saqlash/xabardorlik reyestri). */
export interface LegalReqRow {
  id: number
  country_code: string
  requirement: string
  category: string
  authority: string
  status: 'active' | 'pending' | 'expired'
  updated_at: string
}

export function fetchLegalRegistry(params: FetchParams): Promise<FetchResult<LegalReqRow>> {
  return safeList<LegalReqRow>('/admin/geo/legal-registry', 'requirements', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.requirement} ${r.country_code} ${r.authority}`),
  )
}

/** A11.21 — geo-anomaliya (imkonsiz-harakat, davlat sakrash, VPN-klaster). */
export interface GeoAnomalyRow {
  id: number
  country_code: string
  kind: string
  detail: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  count: number
  detected_at: string
}

export function fetchGeoAnomalies(params: FetchParams): Promise<FetchResult<GeoAnomalyRow>> {
  return safeList<GeoAnomalyRow>('/admin/geo/anomalies', 'anomalies', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.kind} ${r.country_code} ${r.detail}`),
  )
}

/* ── A11.13–17 infra reyestrlari: generic CRUD (JONLI `/admin/geo/*`) ──────────
 * IP-gyo (ip-ranges) / VPN-proxy (vpn-ranges) / Tor (tor-nodes) / DC-routing
 * (dc-routes) / rezidentlik (residency) — bir xil "ro'yxat + qo'shish-form +
 * o'chirish" naqshi (seo/SeoConfigTable). Bitta generic qatlam hammasini xizmat
 * qiladi: ro'yxat fail-soft (safeList), mutatsiya real POST/DELETE. */

/** Minimal shakl — har infra-qatorda `id` bo'ladi (backend qaytaradi). */
export interface GeoConfigRow {
  id: number | string
  [key: string]: unknown
}

/** Ro'yxat fetcher fabrikasi. `searchKeys` — tez qidiruvda solishtiriuvchi maydonlar. */
export function geoList<T extends GeoConfigRow>(
  path: string,
  key: string,
  searchKeys: string[],
): (params: FetchParams) => Promise<FetchResult<T>> {
  return (params: FetchParams) =>
    safeList<T>(path, key, params.signal).then((rows) =>
      toResult(rows, params, (r) => searchKeys.map((k) => String(r[k] ?? '')).join(' ')),
    )
}

export function geoCreate(
  path: string,
  body: Record<string, unknown>,
): Promise<{ ok: boolean; id?: number }> {
  return api.post<{ ok: boolean; id?: number }>(path, body)
}

export function geoDelete(path: string, id: number | string): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`${path}/${encodeURIComponent(String(id))}`)
}

/** A11.14 — VPN/proxy aniqlash yoqilgan holati (app_settings, JONLI). */
export async function fetchVpnDetection(signal?: AbortSignal): Promise<boolean> {
  try {
    const d = await api.get<{ enabled: boolean }>(
      '/admin/geo/vpn-config',
      signal ? { signal } : undefined,
    )
    return Boolean(d?.enabled)
  } catch {
    return false
  }
}

export function setVpnDetection(enabled: boolean): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>('/admin/geo/vpn-config', { enabled })
}

/* ── A11.23 — davlat bo'yicha Premium narxlari (per-country) ──────────
 * REAL `/admin/countries` + greenfield overlay `/admin/geo/premium-restrictions`
 * (`{premium:[{code, enabled, currency, price_month, price_year, trial_days,
 * features:[]}]}` — har element = davlat + value maydonlari YASSI). Saqlash =
 * upsert `POST {code, value}`, o'chirish = `DELETE :code`. Fail-soft: overlay
 * bo'sh (endpoint hali chiqmagan) → ro'yxat baribir to'liq ishlaydi. */

const PREMIUM_PATH = '/admin/geo/premium-restrictions'

/** Bir davlat uchun premium narx-qiymati (upsert `value` bloki). */
export interface PremiumPricing {
  enabled: boolean
  currency: string
  price_month: number
  price_year: number
  trial_days: number
  features: string[]
}

/** Overlay elementi — davlat kodi + yassi `value` maydonlari. */
export type PremiumPricingRow = PremiumPricing & { code: string }

/** REAL davlat qatori + greenfield premium overlay (`pv`, bo'lmasa yo'q). */
export interface CountryPremiumRow extends CountryRow {
  pv?: PremiumPricing
}

/** Bo'sh/noma'lum qiymatni xavfsiz manfiy-bo'lmagan songa keltiradi. */
function toNum(v: unknown): number {
  const n = Number(v)
  return Number.isFinite(n) && n >= 0 ? n : 0
}

/** Yassi overlay elementidan tuzilgan premium qiymat (himoyalangan tiplash). */
function pricingOf(o: Record<string, unknown>): PremiumPricing {
  const f = o.features
  return {
    enabled: Boolean(o.enabled),
    currency: String(o.currency ?? ''),
    price_month: toNum(o.price_month),
    price_year: toNum(o.price_year),
    trial_days: toNum(o.trial_days),
    features: Array.isArray(f) ? f.map(String).filter(Boolean) : [],
  }
}

/** A11.23 — barcha per-davlat premium yozuvlari (fail-soft bo'sh massiv). */
export function listPremiumPricing(signal: AbortSignal): Promise<PremiumPricingRow[]> {
  return safeList<Record<string, unknown>>(PREMIUM_PATH, 'premium', signal).then((rows) =>
    rows
      .map((o) => ({ code: String(o.code ?? '').toUpperCase(), ...pricingOf(o) }))
      .filter((r) => r.code),
  )
}

/** REAL davlatlar sahifasini per-davlat premium overlay bilan ISO2 kod bo'yicha
 *  birlashtiradi. Sahifalash/qidiruv REAL `fetchCountries` dan — overlay bo'sh
 *  bo'lsa ham ro'yxat to'liq (fail-soft). */
export function fetchCountriesPremium(
  params: FetchParams,
): Promise<FetchResult<CountryPremiumRow>> {
  return Promise.all([fetchCountries(params), listPremiumPricing(params.signal)]).then(
    ([page, overlay]) => {
      const map = new Map<string, PremiumPricing>()
      for (const r of overlay) map.set(r.code, r)
      return {
        total: page.total,
        rows: page.rows.map((c) => ({ ...c, pv: map.get(c.code.toUpperCase()) })),
      }
    },
  )
}

/** Upsert — davlat kodi bo'yicha `POST {code, value}`. */
export function upsertPremiumPricing(
  code: string,
  value: PremiumPricing,
): Promise<{ ok: boolean }> {
  return api.post<{ ok: boolean }>(PREMIUM_PATH, { code: code.toUpperCase(), value })
}

/** O'chirish — `DELETE :code`. */
export function deletePremiumPricing(code: string): Promise<{ ok: boolean }> {
  return api.del<{ ok: boolean }>(`${PREMIUM_PATH}/${encodeURIComponent(code.toUpperCase())}`)
}

/** Module-local RU strings (i18n HOT → keyга ko'chiriladi, devApi.ts naqshi). */
export const GEO_STR = {
  // umumiy
  backToList: 'К списку стран',
  flag: '',
  code: 'Код',
  country: 'Страна',
  registrations: 'Регистраций',
  status: 'Статус',
  open: 'Открыта',
  blocked: 'Заблокирована',
  block: 'Заблокировать',
  unblock: 'Разблокировать',
  blockDesc: 'Страна будет закрыта для регистрации и входа; активные сессии её пользователей отзываются.',
  unblockDesc: 'Страна снова станет доступной для регистрации и входа.',
  reverseWindow: 'обратимо: разблокировать в любой момент',
  requestCreated: 'Запрос на подтверждение создан',
  requestFailed: 'Не удалось создать запрос',
  // A11.01 xarita
  mapTitle: 'Карта стран',
  mapNote: 'Обзор покрытия по данным /admin/countries: открытые и заблокированные страны, регистрации.',
  totalCountries: 'Всего стран',
  openCountries: 'Открыто',
  blockedCountries: 'Заблокировано',
  totalUsers: 'Всего регистраций',
  // A11.05 rollout
  rolloutTitle: 'Поэтапное открытие',
  rolloutNote: 'Стадия выката по странам. Заблокированная страна = закрыта; открытая наследует стадию из /admin/geo/rollout.',
  stage: 'Стадия',
  stageClosed: 'Закрыта',
  stageLive: 'Полный доступ',
  // A11.06 funksiya cheklovi
  featureTitle: 'Ограничения по функциям',
  featureNote: 'Отключённые функции по странам (звонки, боты, каналы). Overlay из /admin/geo/feature-restrictions.',
  features: 'Ограниченные функции',
  // A11.07 to'lov cheklovi
  paymentTitle: 'Платёжные ограничения',
  paymentNote: 'Ограничения платежей/премиума по странам. Overlay из /admin/geo/payment-restrictions.',
  payment: 'Ограничение платежей',
  // A11.08 kontent cheklovi
  contentTitle: 'Ограничения контента',
  contentNote: 'Контентные ограничения (18+, стикеры, медиа) по странам. Overlay из /admin/geo/content-restrictions.',
  content: 'Ограничение контента',
  none: '—',
  // A11.09 til/mintaqa
  localeTitle: 'Язык и региональные настройки',
  localeNote: 'Язык по умолчанию, часовой пояс и формат по странам. Overlay из /admin/geo/locale.',
  locale: 'Язык по умолчанию',
  timezone: 'Часовой пояс',
  // A11.10 operatorlar
  operatorsTitle: 'Телефонные операторы',
  operatorsNote: 'Справочник операторов (MCC/MNC) для маршрутизации OTP. Появится с /admin/geo/operators.',
  operator: 'Оператор',
  mccMnc: 'MCC/MNC',
  subscribers: 'Абонентов',
  // A11.12 bloklangan diapazon
  rangesTitle: 'Заблокированные диапазоны номеров',
  rangesNote: 'Заблокированные префиксы/диапазоны MSISDN. Появится с /admin/geo/blocked-ranges.',
  prefix: 'Префикс/диапазон',
  reason: 'Причина',
  blockedAt: 'Заблокирован',
  // A11.19 huquqiy reyestr
  legalTitle: 'Реестр правовых требований',
  legalNote: 'Правовые требования по юрисдикциям (резидентность, хранение, уведомления). Появится с /admin/geo/legal-registry.',
  requirement: 'Требование',
  category: 'Категория',
  authority: 'Орган',
  updatedAt: 'Обновлено',
  statusActive: 'Действует',
  statusPending: 'Ожидает',
  statusExpired: 'Истёк',
  // A11.21 anomaliyalar
  anomaliesTitle: 'Гео-аномалии',
  anomaliesNote: 'Невозможные перемещения, скачки стран, VPN-кластеры. Появится с /admin/geo/anomalies.',
  kind: 'Тип',
  detail: 'Детали',
  severity: 'Уровень',
  count: 'Событий',
  detectedAt: 'Обнаружено',
  sevLow: 'Низкий',
  sevMedium: 'Средний',
  sevHigh: 'Высокий',
  sevCritical: 'Критический',
  // skeleton (infra) — saqlanadi (GeoPlanned hali ishlatishi mumkin)
  plannedBanner: 'Экран готов, ожидает инфраструктуру',
  plannedNeeds: 'Требуется',
  plannedAdapterReady: 'UI и адаптер данных готовы — заполнится, как только появится источник.',
  // A11.13/14/15/16/17 — JONLI infra reyestrlari (CRUD) sarlavha+ustunlar
  ipGeoTitle: 'IP гео-база',
  ipGeoNeeds: 'источник IP→гео (MaxMind/IP2Location) и пайплайн обновления',
  ipGeoNote:
    'Реестр соответствий «IP-диапазон → страна» (источник: MaxMind / IP2Location / ручной ввод) для гео-определения по IP.',
  cidr: 'CIDR / IP',
  sourceCol: 'Источник',
  addIpRange: 'Добавить диапазон',
  vpnTitle: 'Обнаружение VPN/proxy',
  vpnNeeds: 'фид репутации IP (VPN/proxy/datacenter) и движок оценки',
  vpnNote:
    'Диапазоны VPN / proxy / датацентров и действие при совпадении. Тумблер включает применение правил.',
  provider: 'Провайдер',
  actionCol: 'Действие',
  actBlock: 'Блокировать',
  actFlag: 'Отметить',
  actAllow: 'Разрешить',
  addVpnRange: 'Добавить диапазон',
  vpnDetectionLabel: 'Обнаружение VPN/proxy',
  enabledOn: 'Включено',
  enabledOff: 'Выключено',
  torTitle: 'Узлы Tor',
  torNeeds: 'фид списка exit-узлов Tor и синхронизация',
  torNote:
    'Список выходных узлов Tor. Повторное добавление IP обновляет «последнюю активность».',
  ip: 'IP-адрес',
  lastSeen: 'Последняя активность',
  addTorNode: 'Добавить узел',
  dcTitle: 'Маршрутизация ЦОД по регионам',
  dcNeeds: 'реестр дата-центров и правила гео-маршрутизации трафика',
  dcNote: 'Маршрутизация трафика в дата-центр по странам (одна страна → один ЦОД).',
  dcNameCol: 'Дата-центр',
  addDcRoute: 'Добавить маршрут',
  residencyTitle: 'Резидентность данных',
  residencyNeeds: 'карта размещения данных по юрисдикциям и контроль хранилищ',
  residencyNote:
    'Карта размещения данных по юрисдикциям: регион хранения и политика (одна страна → одно правило).',
  storageRegion: 'Регион хранения',
  policyCol: 'Политика',
  addResidency: 'Добавить правило',
  createdCol: 'Создан',
  del: 'Удалить',
  createdOk: 'Запись создана',
  deletedOk: 'Запись удалена',
  opFailed: 'Не удалось выполнить',
  detectionSaved: 'Настройка сохранена',
  // A11.23 — премиум-цены по странам (per-country)
  premiumTitle: 'Премиум-цены по странам',
  premiumNote:
    'Цена Premium по каждой стране: валюта, месяц/год, пробный период, набор функций. Overlay из /admin/geo/premium-restrictions; сохранение — upsert по коду страны.',
  premiumEnabled: 'Premium',
  premiumCurrency: 'Валюта',
  premiumMonth: 'Цена / мес',
  premiumYear: 'Цена / год',
  premiumTrial: 'Пробный период',
  premiumTrialDays: 'Пробный период (дней)',
  premiumFeatures: 'Функции',
  premiumOn: 'Включён',
  premiumOff: 'Выключен',
  premiumEditTitle: 'Премиум-цены',
  premiumEditAction: 'Изменить цены',
  premiumEnabledLabel: 'Premium доступен в стране',
  premiumCurrencyPh: 'USD',
  premiumFeaturesPh: 'no_ads, upload_4gb, animated_emoji',
  premiumFeaturesHint: 'Список функций через запятую.',
  premiumSave: 'Сохранить',
  premiumSaving: 'Сохранение…',
  premiumCancel: 'Отмена',
  premiumSaved: 'Цены сохранены',
  premiumRemove: 'Удалить цены',
  premiumRemoved: 'Цены удалены',
} as const
